import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Grid, Chip, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, Tooltip as MuiTooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ReferenceLine, ReferenceArea,
} from 'recharts';
import { cotAPI } from '../../api/cot';
import useSchemeColors from '../../hooks/useSchemeColors';
import {
  COT_GROUPS, COT_UPPER as UPPER, COT_LOWER as LOWER, disaggregatedLag, disaggregatedWeeks, indexColor,
} from './cotGroups';

const RANGES = [
  { label: '2Y', weeks: 104 },
  { label: '5Y', weeks: 260 },
  { label: '10Y', weeks: 520 },
  // 0 = full history (no window). NOT null: null is the sentinel MUI's exclusive
  // ToggleButtonGroup passes on deselect, which our onChange guard ignores — so a
  // null value here would make the "All" button unselectable.
  { label: 'All', weeks: 0 },
];

const isNil = (v) => v === null || v === undefined;
const fmtInt = (v) => (isNil(v) || Number.isNaN(Number(v)) ? '—' : Number(v).toLocaleString('en-US'));
const fmtSigned = (v) => {
  if (isNil(v) || Number.isNaN(Number(v))) { return '—'; }
  const n = Number(v);
  return `${n > 0 ? '+' : ''}${n.toLocaleString('en-US')}`;
};
const fmtDate = (d) => (typeof d === 'string' ? d.slice(0, 10) : '—');

const fmtIndex = (v) => (isNil(v) ? '—' : Number(v).toFixed(1));

const StatCard = ({ label, value, sub, valueColor = 'text.primary' }) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="h5" sx={{ mt: 0.5, color: valueColor, fontWeight: 600 }}>
      {value}
    </Typography>
    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
  </Paper>
);

// Each group's latest reading, plus open interest.
const Snapshot = ({ latest }) => (
  <Grid container spacing={2} sx={{ mb: 3 }}>
    {COT_GROUPS.map((g) => (
      <Grid key={g.key} size={{ xs: 6, md: 3 }}>
        <StatCard
          label={`${g.label} index`}
          value={fmtIndex(latest[`${g.key}Index`])}
          valueColor={indexColor(g, latest[`${g.key}Index`])}
          sub={`${g.sub} · net ${fmtSigned(latest[`${g.key}Net`])} · w/w ${fmtSigned(latest[`${g.key}NetChange`])}`}
        />
      </Grid>
    ))}
    <Grid size={{ xs: 6, md: 3 }}>
      <StatCard label="Open interest" value={fmtInt(latest.openInterest)} sub="contracts" />
    </Grid>
  </Grid>
);

// The newest weeks, newest first.
const RecentWeeksTable = ({ rows }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Recent weeks</Typography>
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Report date</TableCell>
            <TableCell align="right">Open interest</TableCell>
            {COT_GROUPS.map((g) => <TableCell key={`${g.key}-net`} align="right">{g.label} net</TableCell>)}
            {COT_GROUPS.map((g) => <TableCell key={`${g.key}-idx`} align="right">{g.label} idx</TableCell>)}
            <TableCell>Published</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.reportDate} hover>
              <TableCell>{fmtDate(row.reportDate)}</TableCell>
              <TableCell align="right">{fmtInt(row.openInterest)}</TableCell>
              {COT_GROUPS.map((g) => <TableCell key={`${g.key}-net`} align="right">{fmtSigned(row[`${g.key}Net`])}</TableCell>)}
              {COT_GROUPS.map((g) => (
                <TableCell key={`${g.key}-idx`} align="right" sx={{ color: indexColor(g, row[`${g.key}Index`]), fontWeight: 500 }}>
                  {fmtIndex(row[`${g.key}Index`])}
                </TableCell>
              ))}
              <TableCell>{fmtDate(row.publishedAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
);

const CotPage = () => {
  const c = useSchemeColors(); // Recharts writes SVG attributes: resolved colours, not var()
  const tooltipStyle = { background: c.elevated, border: `1px solid ${c.ink(0.15)}`, borderRadius: 0, color: c.text, fontSize: 12 };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rangeWeeks, setRangeWeeks] = useState(260); // default 5Y

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await cotAPI.getSeries({ market: 'SILVER' });
        if (!cancelled) { setData(res); setError(null); }
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.message || e?.message || 'Failed to load COT data');
        }
      } finally {
        if (!cancelled) { setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const series = useMemo(() => disaggregatedWeeks(data?.series), [data]);
  const groupColor = (g) => (g.color === 'ink' ? c.ink(0.6) : c[g.color]);

  const windowed = useMemo(() => {
    if (!rangeWeeks || series.length <= rangeWeeks) { return series; }
    return series.slice(series.length - rangeWeeks);
  }, [series, rangeWeeks]);

  const latest = series.length ? series[series.length - 1] : null;
  const recent = useMemo(() => series.slice(-26).reverse(), [series]);
  const lagAsOf = disaggregatedLag(data?.asOf, latest?.reportDate);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        <Alert severity="error">Couldn’t load COT data: {error}</Alert>
      </Box>
    );
  }

  if (!latest) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        <Alert severity="info">Disaggregated COT data for silver isn’t available yet.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Commitments of Traders — Silver</Typography>
        <Chip size="small" color="primary" label={`as of ${fmtDate(latest.reportDate)}`} />
      </Box>
      {lagAsOf && (
        <Alert severity="warning" sx={{ mb: 1 }}>{`COT data runs to ${lagAsOf}, but the disaggregated groups stop at ${latest.reportDate}.`}</Alert>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        CFTC Commitments of Traders, disaggregated futures-only — COMEX · {fmtInt(series.length)} weekly reports.
        The COT index places each group’s net position in its trailing 156-week range (0–100);
        {' '}≥{UPPER} and ≤{LOWER} mark positioning extremes.
      </Typography>

      <Snapshot latest={latest} />

      {/* Range selector */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={rangeWeeks}
          onChange={(_e, v) => { if (v !== null) { setRangeWeeks(v); } }}
        >
          {RANGES.map((r) => (
            <ToggleButton key={r.label} value={r.weeks}>{r.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* COT index chart */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          COT Index (0–100)
          <MuiTooltip title={`Each group's net position within its trailing 156-week range. ${COT_GROUPS.map((g) => `${g.label}: ${g.reading}`).join('; ')}.`}>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          </MuiTooltip>
        </Typography>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={windowed} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={c.ink(0.1)} />
            <XAxis stroke={c.ink(0.5)} tick={{ fill: c.ink(0.6), fontSize: 11 }} dataKey="reportDate" tickFormatter={(d) => (typeof d === 'string' ? d.slice(0, 4) : '')} minTickGap={48} />
            <YAxis stroke={c.ink(0.5)} tick={{ fill: c.ink(0.6), fontSize: 11 }} domain={[0, 100]} ticks={[0, LOWER, 50, UPPER, 100]} width={36} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [Number(v).toFixed(1), n]} labelFormatter={fmtDate} />
            <Legend />
            <ReferenceArea y1={UPPER} y2={100} fill={c.ink(0.5)} fillOpacity={0.06} />
            <ReferenceArea y1={0} y2={LOWER} fill={c.ink(0.5)} fillOpacity={0.06} />
            <ReferenceLine y={UPPER} stroke={c.ink(0.4)} strokeDasharray="4 4" />
            <ReferenceLine y={LOWER} stroke={c.ink(0.4)} strokeDasharray="4 4" />
            {COT_GROUPS.map((g) => (
              <Line key={g.key} type="monotone" dataKey={`${g.key}Index`} name={g.label} stroke={groupColor(g)} dot={false} strokeWidth={1.5} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Paper>

      {/* Net positions chart */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Net positions (contracts)</Typography>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={windowed} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={c.ink(0.1)} />
            <XAxis stroke={c.ink(0.5)} tick={{ fill: c.ink(0.6), fontSize: 11 }} dataKey="reportDate" tickFormatter={(d) => (typeof d === 'string' ? d.slice(0, 4) : '')} minTickGap={48} />
            <YAxis stroke={c.ink(0.5)} tick={{ fill: c.ink(0.6), fontSize: 11 }} width={64} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [fmtSigned(v), n]} labelFormatter={fmtDate} />
            <Legend />
            <ReferenceLine y={0} stroke={c.ink(0.4)} />
            {COT_GROUPS.map((g) => (
              <Line key={g.key} type="monotone" dataKey={`${g.key}Net`} name={g.label} stroke={groupColor(g)} dot={false} strokeWidth={1.5} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Paper>

      <RecentWeeksTable rows={recent} />
    </Box>
  );
};

export default CotPage;
